import React, { useEffect } from "react";
import { useState } from "react";
import { Container, Row, Col } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import { getTransactionReportSearchData } from "./../../stores/services/report.service";
import moment from "moment";
import { BsTable } from "react-icons/bs";
import { PDFExport } from "@progress/kendo-react-pdf";
import ReportHeader from "./report-header";
import { numberWithCommas } from "../../utils/customFunction";
import { getFundByAmc } from "../../stores/services/funds.service";
import { useSelector } from "react-redux";
import Select from "react-select";
import ReportTemplates from "../../components/ReportTemplate/ReportTemplate";
import html2pdf from "html2pdf.js";

const UIAN_COLUMNS = [
  { label: "Date", value: "created_at" },
  { label: "Fund ID", value: "amc_code" },
  { label: "Name of Unit Holder", value: "unitholder_name" },
  // { label: "", value: "_empty1" }, // blank col
  { label: "NTN", value: "_ntn" },
  { label: "N", value: "_n1" },
  { label: "Company/ Individual", value: "_type" },
  { label: "N", value: "_n2" },
  // { label: "", value: "_empty2" }, // blank col
  { label: "N", value: "_n3" },
  { label: "N", value: "_n4" },
  // { label: "", value: "_empty3" }, // blank col
  { label: ";", value: "_semi" },
];

const resolveCell = (
  col: { label: string; value: string },
  txn: any,
): string => {
  switch (col.value) {
    case "created_at":
      return txn.created_at ? moment(txn.created_at).format("D/M/YYYY") : "";
    case "amc_code":
      return txn.amc_code ?? "";
    case "unitholder_name":
      return txn.unitholder_name ?? "";
    case "_empty1":
    case "_empty2":
    case "_empty3":
      return "";
    case "_ntn":
      return (
        txn.ntn || txn.cnic || txn.unithodlerCNIC || txn.unithodlerNTN || ""
      );
    case "_n1":
    case "_n2":
    case "_n3":
    case "_n4":
      return "N";
    case "_type":
      return txn.cnic || txn.unithodlerCNIC ? "I" : "C";
    case "_semi":
      return ";";
    default:
      return txn[col.value] ?? "";
  }
};

// Component 
const UIANCreationScreen = () => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const pdfExportRef = React.useRef<PDFExport>(null);
  const theme = useSelector((state: any) => state.ThemeModeReducer.Theme);

  const email = sessionStorage.getItem("email") || "";
  const amc_code = sessionStorage.getItem("amc_code") || "";
  const amc_name = sessionStorage.getItem("amc_name") || "";
  const today = new Date();

  // state 
  const [fund_code, setFund_code] = useState<any>(
    JSON.parse(sessionStorage.getItem("fund_code") || "[]"),
  );
  const [allFunds, setAllFunds] = useState<any[]>([]);
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSearchTrue, setIsSearchTrue] = useState(false);
  const [fontSizeVal, setFontSizeVal] = useState<any>(18);
  const [paperSizeVal, setPaperSizeVal] = useState("A4");
  const [layoutVal, setLayoutVal] = useState("Landscape");
  const [marginPDF, setMarginPDF] = useState("1cm");
  const [scalePDF, setScalePDF] = useState(0.5);
  const [screenSizePerPdf, setScreenSizePerPdf] = useState<any>();
  const [dimensionOfPages, setDimensionOfPages] = useState<any>();
  const [itemsPerPage, setItemsPerPage] = useState<any>(10);
  const [itemsToShow, setItemsToShow] = useState(10);

  const [resFund, setResFund] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [fields, setFields] = useState({
    amc_code,
    fund_code: "",
    from_date: moment(today).format("YYYY-MM-DD"),
    to_date: moment(today).format("YYYY-MM-DD"),
    txn_type: "saleofunit,redemptionofunits,conversionout,conversionin",
    status: "COMPLETED",
  });

  const [selectedValues, setSelectedValues] = useState<any>({
    heading: "",
    sub_heading: "",
    columns: UIAN_COLUMNS.map((c) => c.label),
    balance: [],
  });

  const loadFunds = async () => {
    try {
      const res = await getFundByAmc(
        email,
        amc_code,
        fund_code?.length > 0 ? fund_code : "",
      );
      setAllFunds(res.data.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to load funds");
    }
  };

  useEffect(() => {
    loadFunds();
  }, [fund_code]);

  React.useEffect(() => {
    const sync = () => {
      const fc = sessionStorage.getItem("fund_code");
      setFund_code(fc ? JSON.parse(fc) : undefined);
    };
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(
      "#uian-test",
    ) as NodeListOf<HTMLElement>;
    els.forEach((el) => {
      el.style.setProperty("font-size", `${fontSizeVal}px`, "important");
    });
  }, [fontSizeVal, itemsToShow, selectedValues, isSearchTrue]);

  const fundOptions = allFunds.map((f: any) => ({
    value: f.symbol_code,
    label: `${f.fund_name} - ${f.symbol_code}`,
  }));

  const selectedFundOpt = React.useMemo(() => {
    const f = allFunds.find((x: any) => x.symbol_code === fields.fund_code);
    return f
      ? { value: f.symbol_code, label: `${f.fund_name} - ${f.symbol_code}` }
      : null;
  }, [allFunds, fields.fund_code]);

  const customStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (styles: any, { isFocused, isSelected }: any) => ({
      ...styles,
      background: isFocused
        ? "hsla(291,64%,42%,0.5)"
        : isSelected
          ? "hsla(291,64%,42%,1)"
          : undefined,
    }),
    menuList: (styles: any) => ({
      ...styles,
      background: theme === "dark" ? "#3b3b3b" : "#ffffff",
      color: theme === "dark" ? "white" : "",
    }),
    menu: (base: any) => ({ ...base, zIndex: 100 }),
    control: (base: any, state: any) => ({
      ...base,
      background: theme === "dark" ? "#25273a" : "#f2f2f2",
      borderRadius: "0.4285rem",
      borderColor: state.isFocused
        ? "#1d8cf8"
        : theme !== "light"
          ? "#2b3553"
          : "",
      boxShadow: null,
      "&:hover": { borderColor: state.isFocused ? "" : "#ba54f5" },
    }),
    singleValue: (p: any) => ({
      ...p,
      color: theme === "dark" ? "white" : "black",
    }),
    input: (p: any) => ({ ...p, color: theme === "dark" ? "white" : "black" }),
  };

  const searchData = async () => {
    setIsSearchTrue(false);
    setLoading(true);
    setItemsPerPage(10);
    setItemsToShow(10);
    setReportData([]);

    try {
      const res = await getTransactionReportSearchData(email, fields);

      if (!res.data.data || res.data.data.length === 0) {
        toast.error("No data found");
        setLoading(false);
        return;
      }

      const sorted = [...res.data.data].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      setReportData(sorted);
      setResFund(fields.fund_code);
      setFromDate(fields.from_date);
      setToDate(fields.to_date);
      setIsSearchTrue(true);
      setFontSizeVal(18);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Request Failed!");
    } finally {
      setLoading(false);
    }
  };

  const handleTXT = () => {
    const rows = reportData.map((txn) => {
      const cells = UIAN_COLUMNS.map((col) => {
        const val = resolveCell(col, txn);
        return val === null || val === undefined ? "" : String(val).trim();
      });
      return cells.join(",");
    });

    const content = rows.join("\n") + "\n";
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "UIAN_Creation.txt");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleGeneratePdf = () => {
    const element = contentRef.current;
    const options = {
      filename: "UIAN-Creation-Report.pdf",
      margin: [10, 3, 10, 3],
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 1 },
      jsPDF: {
        unit: "mm",
        format: paperSizeVal || "A4",
        orientation: layoutVal === "Landscape" ? "landscape" : "portrait",
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };
    html2pdf().from(element).set(options).save();
  };

  const handleShowMore = () => setItemsToShow((p) => p + itemsPerPage);

  //Render
  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Container fluid>
              <ToastContainer limit={1} />

              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">UIAN Creation Report</h4>
                </div>
                <div className="card-body">
                  <Row>
                    <Col md="4">
                      <div className="form-group">
                        <label>Select Fund</label>
                        <Select
                          value={selectedFundOpt}
                          label="Select Fund"
                          onChange={(e: any) => {
                            setFields({ ...fields, fund_code: e?.value ?? "" });
                            setReportData([]);
                            setItemsPerPage(10);
                            setItemsToShow(10);
                          }}
                          menuPortalTarget={document.body}
                          menuShouldBlockScroll={true}
                          isClearable
                          isSearchable
                          options={fundOptions}
                          styles={customStyles}
                        />
                      </div>
                    </Col>

                    <Col md="4">
                      <div className="form-group">
                        <label>Settlement Date From</label>
                        <input
                          type="date"
                          style={{
                            colorScheme: theme === "light" ? "light" : "dark",
                          }}
                          value={fields.from_date}
                          className="form-control py-1"
                          onChange={(e) => {
                            setFields({ ...fields, from_date: e.target.value });
                            setReportData([]);
                            setItemsPerPage(10);
                            setItemsToShow(10);
                          }}
                        />
                      </div>
                    </Col>

                    <Col md="4">
                      <div className="form-group">
                        <label>Settlement Date To</label>
                        <input
                          type="date"
                          style={{
                            colorScheme: theme === "light" ? "light" : "dark",
                          }}
                          value={fields.to_date}
                          className="form-control py-1"
                          onChange={(e) => {
                            setFields({ ...fields, to_date: e.target.value });
                            setReportData([]);
                            setItemsPerPage(10);
                            setItemsToShow(10);
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  <div>
                    <button
                      className="btn btn-primary mt-3"
                      onClick={searchData}
                      disabled={Boolean(loading)}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border login-txt spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          />
                          <span className="login-txt"> Loading...</span>
                        </>
                      ) : (
                        <span>Search</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="card mt-3">
                <div className="card-body">
                  {reportData.length > 0 && (
                    <Row>
                      <ReportTemplates
                        ColumnArr={UIAN_COLUMNS}
                        selectedValues={selectedValues}
                        setSelectedValues={setSelectedValues}
                        pdfExportComponent={pdfExportRef}
                        setFontSizeVal={setFontSizeVal}
                        fontSizeVal={fontSizeVal}
                        setPaperSizeVal={setPaperSizeVal}
                        paperSizeVal={paperSizeVal}
                        setLayoutVal={setLayoutVal}
                        layoutVal={layoutVal}
                        setScalePDF={setScalePDF}
                        scalePDF={scalePDF}
                        setMarginPDF={setMarginPDF}
                        marginPDF={marginPDF}
                        setScreenSizePerPdf={setScreenSizePerPdf}
                        screenSizePerPdf={screenSizePerPdf}
                        setDimensionOfPages={setDimensionOfPages}
                        contentRef={contentRef}
                        fileName="UIAN-Creation.pdf"
                        printExcel={() => {
                          // Not added Excel export with UIAN Creation report.
                        }}
                        printTXT={handleTXT}
                        printpdf={handleGeneratePdf}
                      />
                    </Row>
                  )}

                  <div
                    id="printDiv"
                    className="p-2"
                    style={{
                      color: "black",
                      minWidth: "200px",
                      overflowX: "auto",
                      width: "auto",
                    }}
                  >
                    <div
                      style={{
                        width: `${
                          dimensionOfPages?.find(
                            (item: any) => item?.key === paperSizeVal,
                          )?.dimensions[layoutVal === "Portrait" ? 0 : 1]
                        }`,
                        height: "auto",
                        margin: "0 auto",
                        backgroundColor: "white",
                      }}
                      className="border"
                    >
                      <PDFExport
                        paperSize={paperSizeVal || "A4"}
                        margin={{
                          top: "1.5cm",
                          left: "1.5cm",
                          right: "1.5cm",
                          bottom: "6cm",
                        }}
                        scale={scalePDF}
                        repeatHeaders
                        landscape={layoutVal === "Landscape"}
                        fileName={`UIAN-Creation-Report-${today}.pdf`}
                        ref={pdfExportRef}
                      >
                        <div style={{ margin: "0.25in" }} ref={contentRef}>
                          {reportData.length > 0 ? (
                            <>
                              <ReportHeader title="UIAN Creation Report" />

                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "16px",
                                  marginBottom: "12px",
                                  fontSize: "13px",
                                  color: "black",
                                }}
                              >
                                {[
                                  ["Fund", resFund],
                                  [
                                    "From Date",
                                    moment(fromDate).format("DD-MM-YYYY"),
                                  ],
                                  [
                                    "To Date",
                                    moment(toDate).format("DD-MM-YYYY") ===
                                    "Invalid date"
                                      ? toDate
                                      : moment(toDate).format("DD-MM-YYYY"),
                                  ],
                                  [
                                    "Report Date",
                                    moment(today).format("DD-MM-YYYY"),
                                  ],
                                ].map(([title, value]) => (
                                  <span key={title}>
                                    <strong>{title}:</strong>&nbsp;{value}
                                  </span>
                                ))}
                              </div>

                              <div
                                style={{
                                  overflowX: "auto",
                                  overflowY: "hidden",
                                }}
                              >
                                <table
                                  className="report"
                                  style={{ color: "black" }}
                                  cellPadding={6}
                                >
                                  <thead>
                                    <tr>
                                      {UIAN_COLUMNS.map((col, i) => (
                                        <th
                                          key={i}
                                          style={{
                                            padding: "8px 12px",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <span
                                            className="htmltoPdfStyle"
                                            id="uian-test"
                                            style={{
                                              textTransform: "capitalize",
                                            }}
                                          >
                                            {col.label}
                                          </span>
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>

                                  <tbody className="report-table-body">
                                    {reportData
                                      .slice(0, itemsToShow)
                                      .map((txn, rowIdx) => (
                                        <tr key={rowIdx}>
                                          {UIAN_COLUMNS.map((col, colIdx) => (
                                            <td
                                              key={colIdx}
                                              className="htmltoPdfStyle"
                                              id="uian-test"
                                              style={{
                                                padding: "8px 12px",
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {resolveCell(col, txn)}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          ) : (
                            <div className="table-empty">
                              <BsTable className="icon" />
                              <p className="text-center text-primary empty-text">
                                No record found
                              </p>
                            </div>
                          )}
                        </div>
                      </PDFExport>
                    </div>
                  </div>

                  <div className="row align-items-center mt-2">
                    <div className="col-md-4">
                      {reportData.length >= 10 && (
                        <div className="dropdown me-2 mt-2">
                          <button
                            className="btn btn-sm dropdown-toggle border"
                            data-bs-toggle="dropdown"
                            style={{
                              background:
                                theme === "light" ? "white" : "#343a40",
                              color: theme === "light" ? "black" : "white",
                              border:
                                theme === "light"
                                  ? "1px solid #ced4da"
                                  : "1px solid #495057",
                              boxShadow: "none",
                            }}
                          >
                            Per page : {itemsPerPage}
                          </button>
                          <ul className="dropdown-menu">
                            {[10, 25, 50].map((n) => (
                              <li key={n}>
                                <button
                                  className={`dropdown-item ${itemsPerPage === n ? "active" : ""}`}
                                  onClick={() => {
                                    setItemsPerPage(n);
                                    setItemsToShow(n);
                                  }}
                                >
                                  {n}
                                </button>
                              </li>
                            ))}
                            <li>
                              <button
                                className={`dropdown-item ${itemsPerPage === "All" ? "active" : ""}`}
                                onClick={() => {
                                  setItemsPerPage("All");
                                  setItemsToShow(reportData.length);
                                }}
                              >
                                All
                              </button>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="col-md-4 d-flex justify-content-center">
                      {itemsToShow < reportData.length && (
                        <button
                          className="btn btn-primary mt-3"
                          onClick={handleShowMore}
                        >
                          Show More
                        </button>
                      )}
                    </div>

                    <div className="col-md-4 d-flex justify-content-end">
                      {reportData.length > 0 && (
                        <div
                          className="btn btn-sm border"
                          style={{
                            borderRadius: "4px",
                            background: theme === "light" ? "white" : "#343a40",
                            color: theme === "light" ? "black" : "white",
                            border:
                              theme === "light"
                                ? "1px solid #ced4da"
                                : "1px solid #495057",
                            marginTop: "13px",
                            boxShadow: "none",
                            cursor: "default",
                          }}
                        >
                          Total records : {reportData.length}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Container>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default UIANCreationScreen;